
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const QualitySection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const qualityPoints = [
    { icon: 'ri-award-line', text: 'ISO 9001:2015 Kalite Yönetim Sistemi' },
    { icon: 'ri-heart-pulse-line', text: 'FDA onaylı, gıda temasına uygun hammadde' },
    { icon: 'ri-test-tube-line', text: 'Her partide detaylı kalite kontrol testleri' },
    { icon: 'ri-microscope-line', text: 'Gelişmiş laboratuvar analiz sistemleri' },
    { icon: 'ri-loop-left-line', text: 'Sürekli iyileştirme ve geliştirme çalışmaları' },
  ];

  const certifications = [
    { name: 'ISO 9001', desc: 'Kalite Yönetimi' },
    { name: 'ISO 22000', desc: 'Gıda Güvenliği' },
    { name: 'FDA', desc: 'Gıda Onayı' },
  ];

  return (
    <section ref={ref} className="py-16 sm:py-20 lg:py-28 bg-white relative overflow-hidden">
      {/* Background Decorations */}
      <motion.div
        animate={{ x: [0, 15, 0], y: [0, -20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-20 left-10 w-64 h-64 bg-teal/5 rounded-full blur-3xl pointer-events-none"
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-20 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/700b13996eef5b995d9ce61287d0fd03.jpg"
                alt="Kalite Kontrol Laboratuvarı"
                className="w-full h-[350px] sm:h-[450px] lg:h-[550px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/20 to-transparent" />
            </div>

            {/* Floating Cert Badges */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -bottom-6 -right-4 sm:right-6 flex gap-3"
            >
              {certifications.map((cert, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 + i * 0.1 }}
                  whileHover={{ scale: 1.1, y: -4 }}
                  className="bg-white rounded-xl p-3 sm:p-4 shadow-xl border border-gray-100 text-center cursor-pointer"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-gradient-to-br from-teal to-green rounded-lg mx-auto mb-2">
                    <i className="ri-award-line text-white text-sm sm:text-base" />
                  </div>
                  <div className="text-navy text-xs sm:text-sm font-bold">{cert.name}</div>
                  <div className="text-gray-500 text-[10px] sm:text-xs">{cert.desc}</div>
                </motion.div>
              ))}
            </motion.div>

            {/* Decorative */}
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-teal/20 to-transparent rounded-full blur-2xl" />
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {/* The leading slash is escaped to avoid JSX parsing issues */}
            <span className="text-teal text-xs sm:text-sm font-semibold uppercase tracking-wider">
              {'/'}KALİTE GÜVENCE
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mt-4 sm:mt-6 mb-6 sm:mb-8 leading-tight">
              Her Üründe<br />Mükemmellik
            </h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg leading-relaxed mb-8">
              Tüm ürünlerimiz, uluslararası kalite standartlarına uygun olarak üretilir ve her aşamada titizlikle kontrol edilir.
            </p>

            <div className="space-y-4 mb-10">
              {qualityPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="group flex items-start gap-4 p-3 rounded-xl hover:bg-teal/5 transition-colors duration-300 cursor-default"
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-green/10 rounded-lg group-hover:bg-green/20 transition-colors duration-300">
                    <i className={`${point.icon} text-green text-sm`} />
                  </div>
                  <span className="text-gray-7
00 text-sm sm:text-base leading-relaxed">{point.text}</span>
                </motion.div>
              ))}
            </div>

            <Link
              to="/production-quality"
              className="inline-flex items-center gap-2 px-8 sm:px-10 py-3.5 sm:py-4 bg-teal text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-navy hover:shadow-xl hover:-translate-y-0.5 transition-all duration-400 whitespace-nowrap cursor-pointer"
            >
              Kalite Süreçlerimiz
              <motion.i
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                className="ri-arrow-right-line text-lg"
              />
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QualitySection;
